import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import Chart from 'chart.js/auto';
import { AuthService } from '../../services/auth.service';
import { Observable } from 'rxjs';
import { Volunteer } from '../../interfaces/volunteer.interfaces';
import { AssignedActivity } from '../../interfaces/student.interfaces';

@Component({
  selector: 'app-volunteer-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './volunteer-home.component.html',
  styleUrls: ['./volunteer-home.component.scss'],
})
export class VolunteerHomeComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);

  pendingActivities: AssignedActivity[] = [];
  activityHistory: AssignedActivity[] = [];
  expandedComments = new Set<string>();

  private progressChart: Chart | null = null;
  private timeChart: Chart | null = null;

  userInfo$: Observable<Volunteer | null>;
  currentUser: any;

  displayFields: { key: keyof Volunteer; label: string; isDate?: boolean }[] = [];

  constructor() {
    this.userInfo$ = this.authService.getUserInfo();
  }

  ngOnInit(): void {
    this.userInfo$.subscribe((volunteer) => {
      if (!volunteer) {
        return;
      }

      const activities = volunteer.assignedActivities ?? [];

      // Actividades pendientes de asistencia
      this.pendingActivities = activities.filter(
        (activity) =>
          activity.status === 'registered' || activity.status === 'attended'
      );

      // Historial de actividades concluidas
      this.activityHistory = activities.filter(
        (activity) =>
          activity.status === 'validated' || activity.status === 'absent'
      );

      this.cdr.detectChanges();
      this.scheduleChartRender(volunteer);
    });

    this.currentUser = this.authService.getCurrentUser();
    this.generateDisplayFields();
  }

  ngOnDestroy(): void {
    if (this.progressChart) {
      this.progressChart.destroy();
      this.progressChart = null;
    }
    if (this.timeChart) {
      this.timeChart.destroy();
      this.timeChart = null;
    }
  }

  generateDisplayFields(): void {
    this.displayFields = [
      { key: 'folio', label: 'Folio Institucional' },
      { key: 'fullName', label: 'Nombre Completo' },
      { key: 'curp', label: 'CURP' },
      { key: 'occupation', label: 'Ocupación / Profesión' },
      { key: 'mail', label: 'Correo Electrónico' },
      { key: 'phone', label: 'Teléfono de Contacto' },
      { key: 'volunteerStartDate', label: 'Fecha de Registro', isDate: true },
      { key: 'birthDate', label: 'Fecha de Nacimiento', isDate: true },
    ];
  }

  getFieldValue(volunteer: Volunteer, key: keyof Volunteer): any {
    return volunteer[key];
  }

  toggleComment(activityRef: string): void {
    if (this.expandedComments.has(activityRef)) {
      this.expandedComments.delete(activityRef);
    } else {
      this.expandedComments.add(activityRef);
    }
  }

  private scheduleChartRender(volunteer: Volunteer, retries = 5): void {
    setTimeout(() => {
      const pieCanvas = document.getElementById('volProgressChart') as HTMLCanvasElement | null;
      const timeCanvas = document.getElementById('volTimeChart') as HTMLCanvasElement | null;
      if (!pieCanvas || !timeCanvas) {
        if (retries > 0) {
          this.scheduleChartRender(volunteer, retries - 1);
        }
        return;
      }
      this.createChart(volunteer, pieCanvas);
      this.createTimeChart(volunteer, timeCanvas);
    }, 60);
  }

  createChart(volunteer: Volunteer, ctx?: HTMLCanvasElement | null): void {
    const canvas = ctx || (document.getElementById('volProgressChart') as HTMLCanvasElement | null);
    if (!canvas) return;

    const existing = Chart.getChart(canvas);
    if (existing) {
      existing.destroy();
    }
    if (this.progressChart) {
      this.progressChart.destroy();
      this.progressChart = null;
    }

    const completed = this.activityHistory.length;
    const pending = this.pendingActivities.length;

    // Si ambos son 0, mostrar estado base para evitar gráfico en blanco
    const dataValues = completed === 0 && pending === 0 ? [0, 1] : [completed, pending];
    const labels =
      completed === 0 && pending === 0
        ? ['Sin actividades concluidas', 'Sin actividades pendientes']
        : ['Actividades concluidas', 'Actividades pendientes'];

    this.progressChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data: dataValues,
            backgroundColor: ['#24366f', '#f97316'], // Azul ABAQ y Naranja ABAQ
            hoverBackgroundColor: ['#1a2853', '#ea580c'],
            borderColor: ['#ffffff', '#ffffff'],
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 14,
              padding: 16,
              font: { weight: 600, size: 12 },
              color: '#334155',
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => ` ${context.label}: ${context.raw} actividades`,
            },
          },
        },
        cutout: '62%',
      },
    });
  }

  createTimeChart(volunteer: Volunteer, ctx?: HTMLCanvasElement | null): void {
    const canvas = ctx || (document.getElementById('volTimeChart') as HTMLCanvasElement | null);
    if (!canvas) return;

    const existing = Chart.getChart(canvas);
    if (existing) {
      existing.destroy();
    }
    if (this.timeChart) {
      this.timeChart.destroy();
      this.timeChart = null;
    }

    interface TimelinePoint {
      date: Date;
      count: number;
      label: string;
    }

    const points: TimelinePoint[] = [];

    const regDate = volunteer.volunteerStartDate
      ? new Date(volunteer.volunteerStartDate)
      : new Date(Date.now() - 60 * 24 * 3600 * 1000);

    points.push({
      date: regDate,
      count: 0,
      label: 'Registro',
    });

    // Acreditaciones aprobadas
    (volunteer.accreditations ?? [])
      .filter((a) => a.date)
      .forEach((a) => {
        points.push({
          date: new Date(a.date!),
          count: 1,
          label: a.comments || a.type || 'Participación',
        });
      });

    // Actividades asignadas validadas
    (volunteer.assignedActivities ?? [])
      .filter((act) => act.status === 'validated' && (act.dueDate || act.assignedAt))
      .forEach((act) => {
        points.push({
          date: new Date(act.dueDate || act.assignedAt!),
          count: 1,
          label: act.title || 'Actividad',
        });
      });

    points.sort((a, b) => a.date.getTime() - b.date.getTime());

    const labels: string[] = [];
    const cumulativeCount: number[] = [];
    let runningTotal = 0;

    if (points.length <= 1) {
      labels.push(this.formatDateLabel(regDate));
      cumulativeCount.push(0);

      labels.push('Participaciones');
      cumulativeCount.push(this.activityHistory.length);

      labels.push('Hoy');
      cumulativeCount.push(this.activityHistory.length);
    } else {
      points.forEach((pt, index) => {
        runningTotal += pt.count;
        labels.push(index === 0 ? 'Registro' : this.formatDateLabel(pt.date));
        cumulativeCount.push(runningTotal);
      });

      const lastPointDate = points[points.length - 1].date;
      const today = new Date();
      if (today.getTime() - lastPointDate.getTime() > 24 * 3600 * 1000 * 7) {
        labels.push('Hoy');
        cumulativeCount.push(runningTotal);
      }
    }

    this.timeChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Participaciones acumuladas',
            data: cumulativeCount,
            borderColor: '#ea580c', // Naranja vibrante
            backgroundColor: 'rgba(36, 54, 111, 0.12)', // Azul suave institucional translúcido
            borderWidth: 3,
            pointBackgroundColor: '#ea580c',
            pointBorderColor: '#24366f', // Azul borde
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointHoverBackgroundColor: '#24366f',
            pointHoverBorderColor: '#ea580c',
            fill: true,
            tension: 0.35,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
            title: {
              display: true,
              text: 'Total de actividades',
              color: '#64748b',
              font: { weight: 600, size: 11 },
            },
            grid: {
              color: '#f1f5f9',
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#64748b',
              font: { size: 11 },
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              boxWidth: 14,
              font: { weight: 600, size: 12 },
              color: '#24366f',
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => ` Participaciones acumuladas: ${context.raw}`,
            },
          },
        },
      },
    });
  }

  private formatDateLabel(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const month = months[d.getMonth()] || '';
    return `${day} ${month}`;
  }
}
